// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a link that reopens a view: the dataset, the frame, the camera
 * and the layers around the data (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`,
 * slice 1).
 *
 * Additive parameters on the URL grammar (`docs/EMBED_URL_GRAMMAR.md`):
 *
 *   /dataset/<ref>?at=2026-08-27T21:00Z&view=48.20,16.40,3.50,-20,35
 *                  &base=builtin-nasa-blue-marble&over=builtin-nasa-coastlines~white&rt=R2_DASH_…
 *
 * - `at`: a UTC instant the frame must hold. On a stream whose frames are
 *   years, months or days it is written at that precision (`at=2075`,
 *   `at=2003-08`, `at=2026-08-27`), as the date track labels the frame;
 *   it is read as the start of that year, month or day, and the frame whose
 *   label names it is shown (`frameCalendar.frameAtTimeMs`), so the link
 *   comes back to the same frame.
 * - `view`: the camera as latitude, longitude and zoom, then bearing and
 *   pitch when the globe is turned or tilted (owner, 2026-10-05: a link
 *   must bring back what the viewer saw).
 * - `map=flat`: the view was on the flat 2:1 map (AYNI, 2026-10-19,
 *   docs/AYNI_XR_FLAT_MAP_PLAN.md); absent, the globe. Read with `view`.
 * - `base`: the basemap's id, or `none`. Its presence says the link
 *   carries the layer stack; then `over` (repeated, `id` or `id~white` /
 *   `id~black` for a tint) and `rt` (repeated, the real-time overlay
 *   streams, lowest first) say the rest, and what is absent is off.
 *
 * Everything is checked here, so a hand-edited link can at worst be
 * ignored, never fly the globe somewhere absurd or name markup as a
 * layer. Pure: no DOM, no app state.
 */

import { DATASET_PATH_PREFIX, datasetUrlRef, type DatasetUrlRef } from '../../utils/datasetUrl'
import { formatFrameIso, type FramePrecision } from '../frameCalendar'

/** The globe's zoom stays under 10 (owner rule, `globe MAX_ZOOM`). */
export const VIEW_MAX_ZOOM = 9.99
/** MapLibre's steepest pitch on the globe. */
export const VIEW_MAX_PITCH = 85
/** Most overlays and real-time streams a link may name (the room protocol's limits). */
export const VIEW_MAX_OVERLAYS = 8
export const VIEW_MAX_RT = 4

export interface ViewCamera {
  readonly lat: number
  readonly lon: number
  readonly zoom: number
  /** Degrees, -180..180; 0 (north up) when absent. */
  readonly bearing?: number
  /** Degrees, 0..85; 0 (looking straight down) when absent. */
  readonly pitch?: number
  /** AYNI (2026-10-19): on the flat 2:1 map; the globe when absent. */
  readonly flat?: boolean
}

export type ViewTint = 'source' | 'white' | 'black'

/** The layers around the data, as a room shares them (`roomProtocol.RoomLayers`). */
export interface ViewLayers {
  readonly basemapId: string | null
  readonly overlays: readonly { readonly id: string; readonly tint: ViewTint }[]
  /** Real-time overlay streams (dataset ids), lowest first. */
  readonly rt: readonly string[]
}

export interface ViewState {
  /** The frame's instant, epoch ms; null for a dataset without dates. */
  readonly atMs: number | null
  /** AYNI: what the frame's time names (its year, month, day…), so `at` says no more than that; absent: the minute. */
  readonly atPrecision?: FramePrecision | null
  readonly camera: ViewCamera | null
  /** The layer stack; null or absent when the link does not say. */
  readonly layers?: ViewLayers | null
}

/**
 * The altitude (km) `MapRenderer.flyTo` turns back into this zoom: it
 * computes zoom = log2(2R / altitude) with R = 6371 km.
 */
export function altitudeForZoom(zoom: number): number {
  return (6371 * 2) / 2 ** zoom
}

/** The query parameters this module owns. */
export const VIEW_PARAMS = ['at', 'view', 'map', 'base', 'over', 'rt'] as const

/**
 * `2026-08-27T21:00Z`: minutes, unless the instant has seconds; at a
 * year's, month's or day's precision just that (`2075`, `2003-08`).
 */
export function formatAt(ms: number, precision?: FramePrecision | null): string {
  if (precision === 'year' || precision === 'month' || precision === 'day') return formatFrameIso(ms, precision)
  const iso = new Date(ms).toISOString()
  return iso.endsWith(':00.000Z') ? `${iso.slice(0, 16)}Z` : iso.replace('.000Z', 'Z')
}

const wrapDeg = (deg: number): number => ((((deg + 180) % 360) + 360) % 360) - 180

/**
 * Latitude and longitude to 2 decimals (about 1 km), zoom to 2 (a close
 * view must come back as close), then bearing and pitch in whole degrees
 * when either is not zero.
 */
export function formatCamera(camera: ViewCamera): string {
  const lon = wrapDeg(camera.lon)
  // Rounded first, then capped: 9.995 would round up to 10.00, which
  // `parseCamera` refuses.
  const zoom = Math.min(Math.round(camera.zoom * 100) / 100, Math.floor(VIEW_MAX_ZOOM * 100) / 100)
  const base = `${camera.lat.toFixed(2)},${lon.toFixed(2)},${Math.max(0, zoom).toFixed(2)}`
  const bearing = Math.round(wrapDeg(camera.bearing ?? 0))
  const pitch = Math.round(Math.min(Math.max(camera.pitch ?? 0, 0), VIEW_MAX_PITCH))
  return bearing === 0 && pitch === 0 ? base : `${base},${bearing === -0 ? 0 : bearing},${pitch}`
}

const LAYER_ID = /^[A-Za-z0-9._:-]{1,200}$/

/** The query string (with `?`, or '') for a view. */
export function viewQuery(state: ViewState): string {
  const params = new URLSearchParams()
  if (state.atMs !== null && Number.isFinite(state.atMs)) params.set('at', formatAt(state.atMs, state.atPrecision))
  if (state.camera) params.set('view', formatCamera(state.camera))
  if (state.camera?.flat) params.set('map', 'flat')
  const layers = state.layers
  if (layers) {
    params.set('base', layers.basemapId && LAYER_ID.test(layers.basemapId) ? layers.basemapId : 'none')
    for (const o of layers.overlays.slice(0, VIEW_MAX_OVERLAYS)) {
      if (LAYER_ID.test(o.id)) params.append('over', o.tint === 'source' ? o.id : `${o.id}~${o.tint}`)
    }
    for (const id of layers.rt.slice(0, VIEW_MAX_RT)) if (LAYER_ID.test(id)) params.append('rt', id)
  }
  const query = params.toString()
  // URLSearchParams escapes the commas, colons and tildes, which a link
  // pasted into a paper would carry as %2C, %3A and %7E; all are legal in
  // a query.
  return query ? `?${query.replace(/%2C/g, ',').replace(/%3A/g, ':').replace(/%7E/gi, '~')}` : ''
}

/** The full link that reopens this view on `origin`. */
export function buildViewUrl(origin: string, dataset: DatasetUrlRef, state: ViewState): string {
  return `${origin}${DATASET_PATH_PREFIX}${encodeURIComponent(datasetUrlRef(dataset))}${viewQuery(state)}`
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z?)?$/
/** AYNI: a year or a month alone (`2075`, `2003-08`), as a link to a yearly or monthly frame writes it. */
const ISO_YEAR_MONTH = /^(\d{4})(?:-(\d{2}))?$/

/** An `at` value as epoch ms, or null when it is not a real UTC instant. */
export function parseAt(raw: string | null): number | null {
  if (!raw) return null
  const ym = ISO_YEAR_MONTH.exec(raw)
  if (ym) {
    // The start of that year or month, UTC: the frame whose label names it.
    const month = ym[2] === undefined ? 1 : Number(ym[2])
    if (month < 1 || month > 12) return null
    return Date.UTC(Number(ym[1]), month - 1, 1)
  }
  if (!ISO_INSTANT.test(raw)) return null
  // A date alone, or a time without a zone, is read as UTC: a link must
  // not mean a different frame in a different time zone.
  const iso = raw.length === 10 ? `${raw}T00:00:00Z` : raw.endsWith('Z') ? raw : `${raw}Z`
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/** A `view` value as a camera, or null when any part is out of range. */
export function parseCamera(raw: string | null): ViewCamera | null {
  if (!raw) return null
  const parts = raw.split(',')
  if ((parts.length !== 3 && parts.length !== 5) || parts.some((p) => !/^-?\d+(\.\d+)?$/.test(p.trim()))) return null
  const [lat, lon, zoom, bearing = 0, pitch = 0] = parts.map(Number)
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180 || zoom < 0 || zoom > VIEW_MAX_ZOOM) return null
  if (bearing < -180 || bearing > 180 || pitch < 0 || pitch > VIEW_MAX_PITCH) return null
  return parts.length === 5 ? { lat, lon, zoom, bearing, pitch } : { lat, lon, zoom }
}

/** The layer stack a query names, or null when it names none (no `base`, `over` or `rt`). */
export function parseLayers(params: URLSearchParams): ViewLayers | null {
  if (!params.has('base') && !params.has('over') && !params.has('rt')) return null
  const base = params.get('base')
  const overlays: { id: string; tint: ViewTint }[] = []
  for (const raw of params.getAll('over').slice(0, VIEW_MAX_OVERLAYS)) {
    const [id, tint = 'source'] = raw.split('~')
    if (!LAYER_ID.test(id) || (tint !== 'source' && tint !== 'white' && tint !== 'black')) continue
    if (!overlays.some((o) => o.id === id)) overlays.push({ id, tint })
  }
  const rt = [...new Set(params.getAll('rt').filter((id) => LAYER_ID.test(id)))].slice(0, VIEW_MAX_RT)
  return { basemapId: base && base !== 'none' && LAYER_ID.test(base) ? base : null, overlays, rt }
}

/**
 * AYNI: the precision an `at` value was written at: a year (`2075`), a
 * month (`2003-08`) or a day (`2026-08-27`); null for an instant with a
 * time (the minute), or for no valid value.
 */
export function precisionOfAt(raw: string | null): FramePrecision | null {
  if (!raw || parseAt(raw) === null) return null
  if (/^\d{4}$/.test(raw)) return 'year'
  if (/^\d{4}-\d{2}$/.test(raw)) return 'month'
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return 'day'
  return null
}

/**
 * The view a query string asks for; null when it asks for none. A year's,
 * month's or day's `at` keeps its precision (`atPrecision`), so writing
 * the view again gives the same link (Lab space normalises shared views
 * this way).
 */
export function parseViewParams(search: string): ViewState | null {
  const params = new URLSearchParams(search)
  const atMs = parseAt(params.get('at'))
  const atPrecision = precisionOfAt(params.get('at'))
  const parsed = parseCamera(params.get('view'))
  const camera = parsed && params.get('map') === 'flat' ? { ...parsed, flat: true } : parsed
  const layers = parseLayers(params)
  return atMs === null && camera === null && layers === null ? null : { atMs, ...(atPrecision ? { atPrecision } : {}), camera, layers }
}

/** The query string without this module's parameters (with `?`, or ''). */
export function stripViewParams(search: string): string {
  const params = new URLSearchParams(search)
  let changed = false
  for (const name of VIEW_PARAMS) {
    if (params.has(name)) {
      params.delete(name)
      changed = true
    }
  }
  if (!changed) return search
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}
